import { varMax } from '../../../prompt-vars'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { isShort, shortTitle } from '../../../utils/credits'
import { hasFreeLeft, useModelPricing } from '../../../hooks/useModelPricing'
import CreditsShortNote from '../../../components/CreditsShortNote'
import PillInsertChips from '../../../components/PillInsertChips'
import { pickerModelsForTrack } from '../utils/musicModelCaps'
import FreeTierBadge from '../../../components/FreeTierBadge'
import { pluralizeTokens } from '../../../components/TokenPrice'
import { isPromptRequired } from '../utils/generateModelConfig'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { PersonStanding } from 'lucide-react'
import { useAuthLink } from '../../../hooks/useAuthModal'
import { useTranslation } from 'react-i18next'
import type { GenerateSettings } from '../hooks/useGenerateSettings'
import { scrollCaretIntoView } from '../utils/promptScroll'
import PromptPillEditor, { type MentionQuery, type PromptPillEditorHandle, type PillSlots } from './PromptPillEditor'
import type { EnhancePhotoRef } from '../../../api/prompts'
import { phraseForRank, rewritePromptKind, hasPillPhrases, FIRST_FRAME_PHRASES, LAST_FRAME_PHRASE, VIDEO_REF_PHRASE, AUDIO_REF_PHRASE, pillPhraseCaps, promptRefStyleOf, refTokenLabel } from '../../../utils/promptPillPhrases'
import { unmentionedChipIds } from '../../../utils/pillMentions'
import { opAvailable, providerOf } from '../utils/trackOperations'
import { modelsForAudioRoute, supportsTts, TTS_ROUTE } from '../utils/ttsModels'
import { switchSeedanceMode } from '../utils/seedanceMode'
import { switchVideoIntent } from '../utils/videoIntent'
import { inputsAttachedOf } from '../utils/modelParams'
// UploadedFile/UploadedVideo типы и Tooltip теперь живут в UploadThumb.tsx /
// MultiImgRow.tsx — extracted из этого файла. Здесь они больше не нужны.
import SettingsToolsPill from './SettingsToolsPill'
import CreateButton from './CreateButton'
import BarShell, { BarBottom, BarMobileBar, BarPrompt, BarUploads } from '../../../components/bar/BarShell'
import DetachButton from './DetachButton'
import UploadsRow from './UploadsRow'
import SideTooltip from '../../../components/SideTooltip'
// UploadThumb / MultiImgRow всё ещё нужны inline в мобильном bottom-bar
// (.gm__mobile-uploads) — там pre-existing reduced набор слотов, отличный
// от desktop, не рефакторим чтобы не менять mobile behavior.
import UploadThumb from './UploadThumb'
import MultiImgRow from './MultiImgRow'
// SunoLyricsButton + PresentationControls + getAudioFieldAvailability —
// раньше использовались inline в audio-title-wrap / PresentationControls
// рендере. Теперь это всё внутри UploadsRow (single source of truth).
// PROVIDER / TASK_TYPE / BACKEND_MODEL — все использовались только в
// inline pill-рендере, который теперь делегирован SettingsPillsRow.tsx.
// Если понадобятся снова — добавь сюда import { ... } from '../utils/constants'.
import MobileSettingsSheet from './MobileSettingsSheet'
import { useUploadPickerMenu } from '../hooks/useUploadPickerMenu'
import WidgetModelPicker from './WidgetModelPicker'
import { Icon } from '../../../../public/icons/Icon.tsx'
import { useGenerate } from '../../../context/GenerateContext'
import { useVariant } from '../../../context/VariantContext'
import NumberStepper from '../../../components/NumberStepper'
import { useAuth } from '../../../context/AuthContext'
import SceneNotice from './SceneNotice'
import MotionRiskNotice from './MotionRiskNotice'
import SceneMentionMenu, { type SceneMentionHandle } from './SceneMentionMenu'
import { requestSceneRemove, sceneAssetOfUrl, sceneCharacter, useScene } from '../../../api/sceneSelection'
import { assetIdsInPrompt, assetToken } from '../../../prompt-vars/phrases'
import { assetThumb } from '../../../api/assets'
import { nextTypeStop } from '../../../utils/assetMentions'
import { useSceneTools } from '../../../hooks/useSceneTools'
import { getAttachedStyleCard, useAttachedStyleCard } from '../../../api/attachedStyleCard'
import { useAutoPrompt } from '../hooks/useAutoPrompt'
import { useProviders } from '../../../hooks/useProviders'
import { HeyGenTranslateUrlField } from './HeyGenPanels'
import { getHeyGenModelKind } from '../../../api/heygen'
import { isVideoReplaceModel } from '../utils/referenceLimits'
import { hasFrameRefModes } from '../../../utils/frameRefModes'

// Плейсхолдер для prompt-поля в аудио-режимах. Каждая операция имеет свою
// семантику поля (текст песни / стиль кавера / инструкция к extend) —
// общий «Опишите что хотите сгенерировать» юзеру ничего не говорит.
function audioPromptPlaceholder(
	submode: GenerateSettings['audioSubmode'] | undefined,
	operation: string | undefined,
): string {
	if (submode === 'soundtrack') {
		return 'Подсказка о настроении (необязательно) — «спокойное фортепиано» / «энергичный электронный бит»'
	}
	if (submode === 'sounds') {
		return 'Описание звука — «звук грозы в лесу, 8 секунд» / «ретро игровой сигнал»'
	}
	if (submode === 'tts') {
		return 'Текст для озвучки — впишите, что нужно произнести голосом'
	}
	switch (operation) {
		case 'extend':
		case 'upload_extend':
			return 'Как продолжить трек — «добавь припев», «замедли и сделай акустикой в конце»'
		case 'cover':
		case 'upload_cover':
			return 'Стиль ремикса — «в стиле джаза», «медленнее и грустнее», «электронная версия»'
		case 'add_vocals':
			return 'Текст для вокала — готовая лирика или «напой что-то романтичное»'
		case 'add_instrumental':
			return 'Стиль инструментала — «акустическая гитара и скрипка», «электронный бит 120 BPM»'
		case 'mashup':
			return 'Идея смешивания — «плавный переход», «резкий дроп между куплетами»'
		case 'replace_section':
			return 'Чем заменить выделенный кусок — «инструментальное соло», «женский вокал»'
		case 'lyrics':
			return 'Идея для текста песни — «романтичная песня про осень», «рэп про путешествия»'
		default:
			// generate (music)
			return 'Текст песни или идея — «Весёлая поп-песня про кота» или готовый куплет/припев'
	}
}

// AspectIcon перенесён в SettingsPillsRow.tsx (used internally там).
// UploadThumb / MultiImgRow / TextInputBadge перенесены в одноимённые
// файлы (UploadThumb.tsx, MultiImgRow.tsx, TextInputBadge.tsx) для
// переиспользования в UploadsRow.tsx (shared между home и /generate).

// ── Component ────────────────────────────────────────────────
interface GenerateInputBarProps {
	/** Дополнительные элементы в конце строки загрузок (используется в виджете для превью результата) */
	appendUploads?: React.ReactNode
	/** Навигация по режимам — рендерится левой полоской внутри .gm (только в виджете, десктоп) */
	modeNav?: React.ReactNode
	/** Селект нейросетей в нижнем ряду (как на /bots). Студия /generate
	 *  передаёт КАТЕГОРИЮ активной рабочей зоны — в списке только её модели,
	 *  чтобы выбор зоны и выбор инструмента не расходились. */
	modelPickerCategory?: 'image' | 'video' | 'audio'
	/** Показать селект нейронок, когда категорию задавать нечем: в студии
	 *  /generate тип выбирается вкладками ВНУТРИ списка, а не страницей. */
	showModelPicker?: boolean
	/** Навигация по режимам для мобиля — рендерится внутри .gm__mobile-bar рядом с настройками */
	mobileModeNav?: React.ReactNode
	/** Состояние генерации идеи промпта (из useGeneratePrompt) */
	ideaPromptState?: 'idle' | 'loading' | 'animating'
	/** Ref с готовым текстом идеи */
	ideaPromptRef?: React.RefObject<string>
	/** Вызывается после завершения typewriter-анимации */
	onIdeaPromptFinished?: () => void
	/** Скрыть хинт загрузки пока активен тур */
	tourActive?: boolean
	/** Открывает CardModal с preview текущего результата.
	 *  Используется presentation-слотом внутри PresentationControls. */
	onOpenResult?: () => void
	/** Если задан — Send-кнопка вызывает этот хендлер ВМЕСТО gen.handleGenerate.
	 *  Используется на home (см. HomeTemp) для hand-off в /generate: вместо
	 *  прямой генерации навигейтим на /generate (где Provider App-level,
	 *  settings/prompt/uploads уже shared), а autoSend в location.state
	 *  триггерит реальный handleGenerate уже там.
	 *
	 *  Без этого пропса home показывал бы результат прямо здесь, что не
	 *  соответствует UX-плану: home — точка configure, /generate — execute. */
	onSendOverride?: () => void
	/** Открыть Идея-мастер (виджет /bots). Меняет логику кнопки «✦T» в
	 *  мобильном тулбаре на контекстную: ПУСТОЕ поле → мастер (придумать
	 *  с нуля; раньше кнопка при пустом поле была просто выключена),
	 *  есть текст → улучшение промпта (enhance), как раньше. */
	onIdeaWizard?: (e: React.MouseEvent<HTMLButtonElement>) => void
	/** «Повторить движение» (владелец 23.10): пилюля режима рядом с «Придумать идею» — вход в режим замены человека
	 *  в ролике тем же каналом, что /generate?tool=motion. Нет пропа — пилюли нет (режимы карточек, сам режим). */
	onMotionTool?: () => void
	/** Слот ПЕРВЫМ видимым элементом внутри карточки (.gm__content) — виджет
	 *  кладёт сюда строку-пин «Ваш стиль» для узких экранов: она должна жить
	 *  на поверхности карточки, а не в зазоре между шапкой и телом. */
	topSlot?: React.ReactNode
	/** Слот десктоп-картинки: карточка-пин правым блоком в ряду иконок
	 *  (.gm__uploads-aside, рядом с чипами). Мобила/аудио её не используют —
	 *  там роль пина играет topSlot-строка. */
	styleCardSlot?: React.ReactNode
}

// «Больше не показывать» подсказку загрузки (eye-off на pill) — флаг в
// localStorage, как туры/визарды. Per-браузер, для онбординг-хинта достаточно.
const HINT_HIDDEN_KEY = 'umnik.upload_hint_hidden'

export default function GenerateInputBar({
	appendUploads,
	modeNav,
	modelPickerCategory,
	showModelPicker,
	mobileModeNav,
	ideaPromptState,
	ideaPromptRef,
	onIdeaPromptFinished,
	tourActive = false,
	onOpenResult,
	onSendOverride,
	onIdeaWizard,
	onMotionTool,
	topSlot,
	styleCardSlot,
}: GenerateInputBarProps = {}) {
	const { t } = useTranslation()
	const gen = useGenerate()
	// Матрица «что доступно для текущей audio-операции». Используется для
	// дизейбла pill'ов которые Suno проигнорирует/отклонит (например
	// persona для cover, vocal_gender для add_instrumental). Берём из gen
	// напрямую — деструктуризация audioOperation ниже ещё не произошла.
	// audioAvail перенесена в UploadsRow (используется только там для
	// SunoLyricsButton tooltip). Здесь больше не нужна — inline audio-block
	// удалён в пользу <UploadsRow /> вызова.
	const { user } = useAuth()
	// Сцена (персонажи, локации, предметы): чипы-упоминания в промпте, чипы в ряду «Сослаться», барьер «Создать».
	const scene = useScene()
	// «@» и библиотека сцены открыты не всем — рубильник в админке (hooks/useSceneTools).
	const sceneTools = useSceneTools()
	const heroChar = sceneCharacter(scene)
	// Подписи чипов-упоминаний: ассеты сцены + подгруженные меню «@» (чип виден раньше ответа плана сцены).
	const [mentionAssets, setMentionAssets] = useState<Record<string, { name: string; url: string | null; kind: string }>>({})
	const [mention, setMention] = useState<MentionQuery | null>(null)
	const mentionMenuRef = useRef<SceneMentionHandle | null>(null)
	const assetInfo = useMemo(() => {
		const out: Record<string, { name: string; url: string | null; kind: string }> = { ...mentionAssets }
		for (const a of scene?.assets ?? []) out[a.id] = { name: a.name, url: a.avatarUrl, kind: a.kind }
		return out
	}, [mentionAssets, scene])
	// Улучшатель промпта видит «@Имя» вместо токенов (utils/assetMentions): getter — хук объявлен выше по файлу.
	const assetNamesRef = useRef<Record<string, string>>({})
	useEffect(() => {
		assetNamesRef.current = Object.fromEntries(Object.entries(assetInfo).map(([id, a]) => [id, a.name]))
	}, [assetInfo])
	const getAssetNames = useCallback(() => assetNamesRef.current, [])
	// Гость + применена карточка со скрытым промптом (бэк вырезал, prompt_locked)
	// → в зоне промпта показываем CTA вместо текста (см. .gm__prompt-locked).
	const attachedCard = useAttachedStyleCard()
	// Пин-карточка с фото-слотами на Seedance 2.x: слоты живут ТОЛЬКО в режиме
	// «Референсы» — в «Кадрах» payload раскидал бы первые два фото по
	// first/last_frame (живой баг 2026-08-20: сводка показывала «Кадры»).
	// Держим режим, пока пин со слотами активен; пилюля режима при слотах
	// спрятана (SettingsPillsRow → photoSlotsEngaged).
	const pinIsSeedance2 = hasFrameRefModes(gen.selectedModel)
	const pinWantsRefs = pinIsSeedance2 && (attachedCard?.photo_slots?.length ?? 0) >= 2
	useEffect(() => {
		if (!pinWantsRefs) return
		if (gen.settings.seedanceMode !== 'references') gen.updateSetting('seedanceMode', 'references')
	}, [pinWantsRefs, gen.settings.seedanceMode]) // eslint-disable-line react-hooks/exhaustive-deps
	const promptLocked = !user && !!attachedCard?.promptLocked
	const { widgetModels } = useProviders()
	const location = useLocation()
	// Кнопки входа на закрытом поле промпта — окном поверх страницы, как у карточки в виджете (useAuthLink)
	const authLink = useAuthLink()
	const navigate = useNavigate()

	const model = gen.selectedModel

	const currentCategory = location.pathname.startsWith('/bots/images')
		? 'image'
		: location.pathname.startsWith('/bots/video')
			? 'video'
			: location.pathname.startsWith('/bots/audio')
				? 'audio'
				: model?.category
	// Пин трека: операция уходит по провайдеру ТРЕКА → в списке (и мобильном
	// ряду «Модель») только его модели; недоступные версии (9.5) скрыты.
	const availableModels = pickerModelsForTrack(
		modelsForAudioRoute(widgetModels, location.pathname).filter((m) => m.category === currentCategory),
		gen.sourceTrack?.model ?? null,
		!!gen.sourceTrack && !!gen.audioOperation && gen.audioOperation !== 'generate',
	)
	const {
		prompt,
		setPrompt,
		settings,
		// Здесь настройки меняет только человек (подрежим музыки, негативный промпт, панели HeyGen) — с отметкой
		// «изменил пользователь» (журнал генерации в админке); автоподстройки бара идут через gen.updateSetting.
		userUpdateSetting: updateSetting,
		isLoading,
		isUploading,
		hasPendingUploads,
		negativePromptOpen,
		setNegativePromptOpen,
		currentCost,
		costPending,
		costIssue,
		error,
		clearError,
		// Upload-related state — большинство теперь читается ВНУТРИ UploadsRow.tsx,
		// здесь остаются только те, что используются в InputBar для других целей
		// (validation, idea-prompt context, audio-source-track).
		uploadedImage,
		referenceImages,
		swapImage,
		uploadedVideo,
		referenceVideo,
		selectedVideoDuration,
		fileInputRef,
		swapInputRef,
		videoInputRef,
		handleImageUpload,
		handleMultipleImageUpload,
		handleSwapImageUpload,
		handleVideoUpload,
		setUploadedImage,
		setSwapImage,
		setUploadedVideo,
		setReferenceImages,
		setSelectedVideoDuration,
		handleGenerate,
		requiresVideo,
		requiresImage,
		requiresTwoImages,
		supportsMultipleImages,
		showStyleHint,
		setShowStyleHint,
		isAudio,
		isPresentation,
		audioOperation,
	} = gen
	const { variantChips, activeVariantKey, selectVariant, selectCustomVariant, promptAnim, latestNonce, activeUserInput, cardUserValue, setUserValue, activeUsesValue, activePhotoSlotNums } = useVariant()

	// Пикер источника файла у слотов («Загрузить / Из моих генераций») —
	// единый механизм с tool-режимом (см. useUploadPickerMenu).
	const { makeShowPicker, pickerPortal, galleryModal } = useUploadPickerMenu({
		hideCharacter: attachedCard?.hide_character,
		photoLimit: attachedCard?.user_photo_limit,
	})

	const textareaRef = useRef<HTMLTextAreaElement>(null)
	// Промпт живёт в contenteditable-редакторе с пилюлями фото (2026-08-23);
	// textareaRef остаётся для старых эффектов (там всё под if (ta)).
	const pillEditorRef = useRef<PromptPillEditorHandle>(null)
	const negRef = useRef<HTMLTextAreaElement>(null)
	const [mobileSheetOpen, setMobileSheetOpen] = useState(false)

	const typewriterRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	// Приложенные фото для улучшателя — список чипов «Сослаться на фото» живёт
	// ниже по файлу; хук читает его через ref в момент клика (владелец 04.09:
	// «Улучшить промпт» не знал о фото и принимал id модели за сюжет).
	const enhancePhotosRef = useRef<EnhancePhotoRef[]>([])
	const getEnhancePhotos = useCallback(() => enhancePhotosRef.current, [])
	const autoPrompt = useAutoPrompt({
		prompt,
		modelId: model?.id ?? '',
		isVideo: gen.isVideo,
		modelName: model?.name,
		getPhotos: getEnhancePhotos,
		getAssetNames,
	})

	useEffect(() => {
		const ta = textareaRef.current

		if (autoPrompt.state === 'animating') {
			const enhanced = autoPrompt.enhancedRef.current
			if (!enhanced) return

			setPrompt('')
			let i = 0
			let cancelled = false

			function typeNext() {
				if (cancelled) return
				// Упоминание ассета — одним шагом: по буквам в поле бежал бы @{uuid}@.
				i = nextTypeStop(enhanced, i)
				setPrompt(enhanced.slice(0, i))
				if (i < enhanced.length) {
					typewriterRef.current = setTimeout(typeNext, 16)
				} else {
					typewriterRef.current = null
					if (ta) autoResize(ta)
					autoPrompt.finishAnimation()
				}
			}

			typewriterRef.current = setTimeout(typeNext, 16)
			return () => {
				cancelled = true
			}
		} else if (autoPrompt.state === 'idle') {
			if (typewriterRef.current) {
				clearTimeout(typewriterRef.current)
				typewriterRef.current = null
			}
		}
	}, [autoPrompt.state]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		const ta = textareaRef.current
		if (ideaPromptState !== 'animating') return

		const text = ideaPromptRef?.current ?? ''
		if (!text) return

		setPrompt('')
		let i = 0
		let cancelled = false

		function typeNext() {
			if (cancelled) return
			i = nextTypeStop(text, i)
			setPrompt(text.slice(0, i))
			if (i < text.length) {
				typewriterRef.current = setTimeout(typeNext, 16)
			} else {
				typewriterRef.current = null
				if (ta) autoResize(ta)
				onIdeaPromptFinished?.()
			}
		}

		typewriterRef.current = setTimeout(typeNext, 16)
		return () => {
			cancelled = true
		}
	}, [ideaPromptState]) // eslint-disable-line react-hooks/exhaustive-deps

	// Живой ref на текущий промпт — для минимального диффа при вставке вариации
	// (эффект не пере-подписывается на каждое нажатие клавиши).
	const promptRef = useRef(prompt)
	useEffect(() => {
		promptRef.current = prompt
	})

	// Правка ЮЗЕРА (редактор эмитит только ввод): чип ассета стёрли — ассет выходит из сцены, как у Artlist
	// («стопка = чипы в тексте»). Программная замена промпта (карточка, анимация) сюда не попадает — сцена липкая.
	const onPromptEdit = useCallback(
		(v: string) => {
			const before = assetIdsInPrompt(promptRef.current)
			promptRef.current = v
			setPrompt(v)
			if (!before.length) return
			const after = assetIdsInPrompt(v)
			// keepPrompt: текст уже новый (v) — сцена его не переписывает. Иначе снятие брало промпт прошлого
			// рендера и возвращало его: «выделить всё и напечатать» оставляло старый текст без чипа.
			for (const id of before) if (!after.includes(id)) requestSceneRemove(id, true)
		},
		[setPrompt],
	)

	// Анимированная вставка демо-переменной: клик по чипу → selectVariant →
	// promptAnim. Печатаем ТОЛЬКО изменившийся кусок (общий префикс/суффикс
	// оставляем на месте) «беглой дорожкой», + автоскролл к месту вставки.
	useEffect(() => {
		if (!promptAnim) return
		const myNonce = promptAnim.nonce
		const ta = textareaRef.current
		const target = promptAnim.target
		const cur = promptRef.current
		let pre = 0
		const maxPre = Math.min(cur.length, target.length)
		while (pre < maxPre && cur.charCodeAt(pre) === target.charCodeAt(pre)) pre++
		let suf = 0
		const maxSuf = Math.min(cur.length - pre, target.length - pre)
		while (suf < maxSuf && cur.charCodeAt(cur.length - 1 - suf) === target.charCodeAt(target.length - 1 - suf)) suf++
		const head = target.slice(0, pre)
		const mid = target.slice(pre, target.length - suf)
		const tail = target.slice(target.length - suf)
		if (typewriterRef.current) {
			clearTimeout(typewriterRef.current)
			typewriterRef.current = null
		}
		// Устаревший запуск (карточку сменили после этого selectVariant) — выходим,
		// не трогая промпт, иначе перезатрём свежий промпт выбранной карточки.
		if (myNonce !== latestNonce.current) return
		// Мгновенно убираем старую вставку; печатаем новую между head и tail.
		setPrompt(head + tail)
		if (ta) autoResize(ta)
		let i = 0
		function typeNext() {
			// Смена карточки/вариации СИНХРОННО двигает latestNonce → выходим,
			// чтобы старая печать не перезатёрла промпт выбранной карточки.
			if (myNonce !== latestNonce.current) return
			i++
			setPrompt(head + mid.slice(0, i) + tail)
			if (ta) {
				autoResize(ta)
				scrollCaretIntoView(ta, pre + i)
			}
			pillEditorRef.current?.scrollPosIntoView(pre + i)
			if (i < mid.length) typewriterRef.current = setTimeout(typeNext, 8) // 8мс/симв — 2× быстрее
			else typewriterRef.current = null
		}
		if (mid.length) typewriterRef.current = setTimeout(typeNext, 8)
		return () => {
			if (typewriterRef.current) {
				clearTimeout(typewriterRef.current)
				typewriterRef.current = null
			}
		}
	}, [promptAnim?.nonce]) // eslint-disable-line react-hooks/exhaustive-deps

	// isMidjourney / isKling / isI2V / showSound / useDurationSlider — все эти
	// флаги жили здесь когда pill'ы рендерились inline. Теперь они вычисляются
	// внутри SettingsPillsRow, где и нужны. Здесь остаётся только то, что
	// используется для upload-слотов и тултипов (showImageUpload, paramHints).
	const maxImages = model?.maxImages ?? 1
	const showImageUpload =
		requiresImage || !!model?.supportsImageInput || requiresTwoImages || supportsMultipleImages
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const paramHints = ((model ?? {}) as Record<string, any>).paramHints || {}

	const DEFAULT_HINTS: Record<string, string> = {
		image:
			'Референсное изображение. Аватары, фон и другие элементы создаются на основе этого изображения.',
		video:
			'Референсное видео. Движения аватара повторяют движения из этого видео. Поддерживаются .mp4/.mov, макс. 100 МБ, длительность 3–30 сек.',
		swap_image: 'Изображение лица для замены. Используйте чёткое фото лица анфас без очков.',
		end_image:
			'Изображение последнего кадра. Модель плавно перейдёт от первого кадра к этому изображению.',
		reference_video:
			'Видео-референс для стиля съёмки или редактирования. Поддерживаются .mp4/.mov.',
		reference_images:
			'Референсные изображения для стиля или аватаров. Модель учтёт их при генерации.',
		aspectRatio:
			'Соотношение сторон итогового медиафайла. 16:9 — горизонтальное, 9:16 — вертикальное (Reels/TikTok), 1:1 — квадрат.',
		duration: 'Длительность генерируемого видео в секундах.',
		resolution:
			'Качество и разрешение итогового файла. Более высокое разрешение увеличивает стоимость генерации.',
		sound: 'Автоматическая генерация звукового сопровождения: фоновые шумы, атмосфера и эффекты.',
		negativePrompt:
			'Что не должно появляться в результате. Перечислите нежелательные элементы через запятую.',
		promptOptimizer:
			'Модель автоматически улучшает ваш промт для получения более точного и качественного результата.',
		speed:
			'Скорость генерации. Turbo — быстрее и дешевле, Relaxed — медленнее, но экономичнее при высокой нагрузке.',
		stylization:
			'Насколько сильно модель применяет свой художественный стиль. 0 — близко к промту, 1000 — максимальная стилизация.',
		characterOrientation:
			'Ориентация аватара. «По изображению» — сохраняет позу (макс. 10 сек). «По видео» — повторяет ориентацию из видео (макс. 30 сек).',
		keepOriginalSound: 'Сохранить оригинальный звук из загруженного видео в итоговом результате.',
		googleSearch: 'Модель использует актуальную информацию из Google для более точной генерации.',
		outputFormat:
			'Формат итогового изображения. JPG — меньше размер, PNG — без потерь, WebP — оптимальный баланс.',
		safetyFilter:
			'Уровень фильтрации контента. Минимальный — пропускает больше, Строгий — блокирует больше.',
		videoReferenceType:
			'«Стиль и камера» — перенос стиля съёмки. «Редактирование» — прямое редактирование видео.',
	}

	const hint = (key: string): string | undefined =>
		paramHints[key]?.description || DEFAULT_HINTS[key]

	const prevSettingsRef = useRef<string | null>(null)
	useEffect(() => {
		const current = [
			settings.aspectRatio,
			settings.resolution,
			settings.outputFormat,
			settings.duration,
			settings.characterOrientation,
		].join('|')
		if (prevSettingsRef.current !== null && prevSettingsRef.current !== current) clearError()
		prevSettingsRef.current = current
	}, [
		settings.aspectRatio,
		settings.resolution,
		settings.outputFormat,
		settings.duration,
		settings.characterOrientation,
		clearError,
	])

	function autoResize(el: HTMLTextAreaElement) {
		el.style.height = 'auto'
		el.style.height = el.scrollHeight + 'px'
	}

	const handleNegativePromptToggle = useCallback(() => {
		setNegativePromptOpen(!negativePromptOpen)
	}, [negativePromptOpen, setNegativePromptOpen])

	// Для параметрических audio-операций (extend/cover/add_vocals/add_instrumental)
	// prompt опциональный — Suno сам напишет текст под стиль, либо юзер
	// меняет только жанр/инструментал. Требовать ввод текста бессмысленно.
	const isParametricAudio = !!(
		gen.audioOperation &&
		gen.audioOperation !== 'generate' &&
		gen.sourceTrack
	)
	const promptRequired = isPromptRequired(model ?? null, { requiresVideo, isParametricAudio })
	// Модель текст не читает вообще (face-swap отбрасывает промпт на бэке) —
	// поле прячем и объясняем почему. Это НЕ то же, что promptRequired=false:
	// у видео и операций над треком промпт опционален, но полезен, и остаётся.
	const modelIgnoresPrompt = !!model?.noPrompt

	// add_vocals в оригинальном Suno работает ТОЛЬКО на инструментальных
	// треках — Suno отклонит запрос если источник с вокалом (lyrics != null).
	// Предотвращаем сабмит на нашей стороне вместо того чтобы ждать ошибку
	// от Suno, у юзера warning с CTA «Cover-версия» уже показан в pin'е.
	const addVocalsBlocked =
		gen.audioOperation === 'add_vocals' &&
		!!gen.sourceTrack?.lyrics &&
		gen.sourceTrack.lyrics.trim().length > 0

	// Suno: prompt-лимит зависит от audioCustomMode. 200 без custom_mode
	// (Suno API trip), 5000 с custom_mode. Submode 'sounds' идёт через
	// отдельный endpoint /sounds — под этот лимит не попадает. Используется
	// и в maxLength textarea (hard блок при типизации/paste), и в isDisabled
	// (submit-кнопка серая при превышении). Дополнительно validate() в
	// useGenerationFlow ловит этот же случай как defense-in-depth.
	const audioPromptLimit =
		!isAudio || settings.audioSubmode === 'sounds'
			? null
			: settings.audioSubmode === 'tts'
				? 500 // /voice/tts: текст ≤ 500 (TTS_MAX_CHARS на бэке)
				: settings.audioSubmode === 'soundtrack'
					? 1024 // Mureka /soundtrack: prompt ≤ 1024 (guidance-подсказка, не текст песни)
					: settings.audioCustomMode ? 5000 : 200
	const isAudioOverLimit =
		audioPromptLimit !== null && prompt.length > audioPromptLimit

	// ── Пилюли фото в промпте (2026-08-23) ──
	// Ранг пилюли = позиция файла в референсах: активные слоты карточки по
	// возрастанию (mirrorSlots), без карточки — просто загруженные референсы.
	const pillKind: 'image' | 'video' = gen.isVideo ? 'video' : 'image'
	const pillSlots: PillSlots = useMemo(() => {
		const named = attachedCard?.photo_slots
		// Вход считается «в игре», только если ТЕКУЩАЯ модель его потребляет:
		// смена модели оставляет файлы в стейте, но пилюли чужих входов должны
		// честно гаснуть (янтарь «не загружено»), а не выглядеть рабочими.
		// urls — СЫРЫЕ из состояния (файлы живут при смене модели); применимость
		// входов к текущей модели — отдельно в caps: неприменимые пилюли редактор
		// прячет целиком, при возврате к модели они возвращаются сами.
		const caps = pillPhraseCaps(model, settings.seedanceMode, !!(named && named.length >= 2), supportsMultipleImages)
		const photos =
			named && named.length >= 2
				? named
						.map((slot, i) => ({ slot, i }))
						.filter(({ i }) => activePhotoSlotNums == null || activePhotoSlotNums.includes(i + 1))
						.map(({ slot, i }, r) => ({ rank: r + 1, label: slot?.label || undefined, url: gen.slotImages[i]?.url || null }))
				: referenceImages.map((f, i) => ({ rank: i + 1, label: undefined, url: f.url || null }))
		return {
			kind: pillKind,
			photos,
			caps,
			// Подписи пилюль — токеном выбранной модели (utils/promptPillPhrases).
			refStyle: promptRefStyleOf(model),
			firstFrame: { url: uploadedImage?.url || null },
			lastFrame: { url: gen.endImage?.url || null },
			character: heroChar ? { url: heroChar.avatarUrl || null, name: heroChar.name } : undefined,
			// Чипы-упоминания ассетов сцены (@{uuid}@): имя, миниатюра, тип.
			assets: assetInfo,
			videoRef: { url: gen.referenceVideo?.url || gen.referenceVideos?.[0]?.url || null },
			audioRef: { url: gen.referenceAudios?.[0]?.url || null, name: gen.referenceAudios?.[0]?.file?.name || undefined },
		}
	}, [attachedCard?.photo_slots, activePhotoSlotNums, gen.slotImages, referenceImages, uploadedImage?.url, gen.endImage?.url, heroChar, assetInfo, pillKind, gen.referenceVideo?.url, gen.referenceVideos, gen.referenceAudios, model, settings.seedanceMode, supportsMultipleImages])

	// Чипы вставки: фото-референсы (до 4; именованные поля карточки — все, их обещает карточка) + жёсткие входы модели — первый кадр
	// (I2V: одиночное фото), последний кадр, персонаж (владелец 23.08: «для
	// видео не используется такой подход?» — используется, через эти чипы).
	const insertChips = useMemo(() => {
		const chips: Array<{ id: string; label: string; cls: string; url: string | null; phrase: string; popTitle: string; emptyHint: string }> = []
		// Seedance 2 в «Кадрах» шлёт первый/последний кадр — референсы (и их чипы)
		// в этом режиме не в игре, даже если файлы остались от режима «Референсы».
		const seedanceFrames = pinIsSeedance2 && (settings.seedanceMode ?? 'frames') === 'frames'
		for (const ps of (pillSlots.caps?.photo ? pillSlots.photos.slice(0, attachedCard?.photo_slots?.length ? varMax('photo') : 4) : [])) {
			// Чипы — только по ЗАГРУЖЕННЫМ входам (владелец 23.08: пустые чипы
			// сбивали — «слоты очистились, а ряд остался»). Слот карточки без
			// фото — исключение: карточка обещает слот, чип-приглашение уместен.
			if (!ps.url && !attachedCard?.photo_slots?.length) continue
			// Фото ассета сцены представляет чип ассета (@Имя): отдельный «Image N» дублировал бы его и
			// ругался «не упомянуто», хотя ассет в промпте есть.
			if (ps.url && sceneAssetOfUrl(scene, ps.url)) continue
			chips.push({
				id: `photo${ps.rank}`, label: `${refTokenLabel({ kind: 'photo', rank: ps.rank }, pillSlots.refStyle) ?? `Image ${ps.rank}`}${ps.label ? ` · ${ps.label}` : ''}`, cls: `p${ps.rank}`,
				url: ps.url || null, phrase: phraseForRank(ps.rank, pillKind) || '', popTitle: `Image ${ps.rank}${ps.label ? ` · ${ps.label}` : ''}`,
				emptyHint: 'Слот пуст — сначала загрузите фото в ряду выше, потом ссылайтесь на него в промпте.',
			})
		}
		// Одиночное фото (первый кадр у видео / приложенное фото у картинок) —
		// когда референс-ряда нет: у I2V-моделей это единственный вход. Seedance 2
		// в режиме «Кадры» — тоже первый/последний кадр, а не референсы.
		if (uploadedImage?.url && pillSlots.caps?.first_frame && !requiresTwoImages) {
			chips.push({
				id: 'first_frame', label: gen.isVideo ? 'Первый кадр' : 'Ваше фото', cls: 'frame1',
				url: uploadedImage.url, phrase: FIRST_FRAME_PHRASES[pillKind],
				popTitle: gen.isVideo ? 'Первый кадр' : 'Ваше фото',
				emptyHint: '',
			})
		}
		if ((model?.supportsEndImage || seedanceFrames) && gen.endImage?.url && !attachedCard?.hide_end_image) {
			chips.push({ id: 'last_frame', label: 'Последний кадр', cls: 'frame2', url: gen.endImage.url, phrase: LAST_FRAME_PHRASE, popTitle: 'Последний кадр', emptyHint: '' })
		}
		// Ассеты сцены: клик вставит чип-упоминание (сервер свяжет имя с фото ассета в запросе).
		if (!attachedCard?.hide_character) {
			for (const a of scene?.assets ?? []) {
				const kindLabel = a.kind === 'location' ? 'Локация' : a.kind === 'prop' ? 'Предмет' : 'Аватар'
				chips.push({ id: `asset:${a.id}`, label: a.name, cls: 'asset', url: a.avatarUrl, phrase: assetToken(a.id), popTitle: `${kindLabel} · ${a.name}`, emptyHint: '' })
			}
		}
		// Видео-референс (Seedance «Референсы» и другие модели с видео-входом):
		// пилюля «Видео» = the reference video.
		const videoUrl = pillSlots.caps?.video_ref ? gen.referenceVideo?.url || gen.referenceVideos?.[0]?.url || null : null
		if (videoUrl) {
			chips.push({ id: 'video_ref', label: refTokenLabel({ kind: 'video_ref' }, pillSlots.refStyle) ?? 'Видео', cls: 'video', url: videoUrl, phrase: VIDEO_REF_PHRASE, popTitle: 'Видео-референс', emptyHint: '' })
		}
		// Аудио-референс: пилюля честно показывает, ЧТО загружено (в поповере можно
		// послушать); ординального управления аудио у моделей нет — фраза нейтральна.
		const audioUrl = pillSlots.caps?.audio_ref ? gen.referenceAudios?.[0]?.url || null : null
		if (audioUrl) {
			chips.push({ id: 'audio_ref', label: refTokenLabel({ kind: 'audio_ref' }, pillSlots.refStyle) ?? 'Аудио', cls: 'audio', url: audioUrl, phrase: AUDIO_REF_PHRASE, popTitle: 'Аудио-референс', emptyHint: '' })
		}
		return chips
	}, [pillSlots, pillKind, requiresTwoImages, uploadedImage?.url, gen.endImage?.url, model?.supportsEndImage, attachedCard?.photo_slots?.length, attachedCard?.hide_end_image, attachedCard?.hide_character, scene, gen.isVideo, pinIsSeedance2, settings.seedanceMode, gen.referenceVideo?.url, gen.referenceVideos, gen.referenceAudios])
	useEffect(() => {
		// Имя = фраза-ссылка в тексте («Image 1», «the first frame image»); подпись
		// фото-слота — без дубля имени (у чипа label = «Image 1 · Ваше фото»).
		enhancePhotosRef.current = insertChips
			.filter((c) => c.url && c.phrase)
			.map((c) => {
				const pm = /^p(\d)$/.exec(c.cls)
				// Ассет сцены: улучшатель видит «@Имя» (как в тексте после подмены токенов), роль — по типу.
				if (c.cls === 'asset') {
					const a = scene?.assets.find((x) => `asset:${x.id}` === c.id)
					return { name: `@${c.label}`, label: c.popTitle, kind: a?.kind === 'character' ? 'character' : 'photo' } as const
				}
				// Вид файла — улучшатель назначает роль (что брать: облик, движение, звук).
				const kind = c.cls === 'video' ? 'video' : c.cls === 'audio' ? 'audio' : c.cls === 'char' ? 'character' : c.cls.startsWith('frame') ? 'frame' : 'photo'
				return { name: c.phrase, label: pm ? pillSlots.photos[Number(pm[1]) - 1]?.label || '' : c.label, kind } as const
			})
	}, [insertChips, pillSlots.photos, scene])

	// Поповер на чипах «Сослаться на фото» живёт в общем PillInsertChips.

	// Смена типа модели картинка↔видео — переписать фразы пилюль под новый тип
	// («the first image» ↔ «the first reference image»), не трогая остальной текст.
	const prevPillKind = useRef(pillKind)
	useEffect(() => {
		if (prevPillKind.current === pillKind) return
		prevPillKind.current = pillKind
		const cur = promptRef.current
		if (cur && hasPillPhrases(cur)) setPrompt(rewritePromptKind(cur, pillKind))
	}, [pillKind]) // eslint-disable-line react-hooks/exhaustive-deps

	// Именованные фото-слоты пина: «Создать» ждёт все — промпт карточки
	// командует «первым/вторым фото» (условие активности = UploadsRow).
	const pinSlots = attachedCard?.photo_slots
	const namedSlotsMissing =
		!!pinSlots && pinSlots.length >= 2 &&
		maxImages >= pinSlots.length && !requiresTwoImages &&
		pinSlots.some(
			(_, i) => (activePhotoSlotNums == null || activePhotoSlotNums.includes(i + 1)) && !gen.slotImages[i]?.url,
		)

	// Активный стиль подставляет {value}, значение пусто → блок (min/max из
	// админки — плейсхолдер-ориентир, не значение).
	const userValueMissing = !!activeUserInput && activeUsesValue && !cardUserValue.trim()

	// Не хватает токенов — кнопка выключена до нажатия, под баром строка «Пополнить баланс» (стандарт utils/credits;
	// currentCost — точная цена выбранных настроек, 0 у бесплатного тира — тогда не блокируем).
	// Параметры бесплатного тира — те же, что у FreeTierBadge (он получает готовый ответ, сам не грузит).
	const freeTierType = model?.category === 'video' ? 'video' : model?.category === 'image' ? 'image' : null
	const freeTierParams = model && freeTierType
		? (freeTierType === 'video'
			? { duration: settings.duration, resolution: settings.resolution, aspect_ratio: settings.aspectRatio === 'match_input_image' ? null : settings.aspectRatio }
			: { resolution: settings.resolution, quality: model.qualityLevels ? settings.quality : null, aspect_ratio: settings.aspectRatio === 'match_input_image' ? null : settings.aspectRatio, output_format: settings.outputFormat })
		: null
	const freeTier = useModelPricing(model?.provider, model ? (model.backendModel || model.id) : null, freeTierType, freeTierParams, isLoading, !!user)
	const creditsBalance = Number(user?.credits_balance ?? 0)
	// Пока у модели есть бесплатные генерации, сервер спишет 0 — не блокируем.
	// Плата за секунды входного видео: пока сервер меряет видео на входе, цены со входом ещё нет — запуск ждёт; не
	// измерилось — закрыт с причиной (иначе сервер отказал бы уже после нажатия).
	const costHold = costPending || !!costIssue
	const creditsShort = !!user && !costHold && !hasFreeLeft(freeTier) && isShort(creditsBalance, currentCost)
	const creditsTitle = costIssue ?? (costPending ? 'Считаем цену с видео на входе…' : creditsShort ? shortTitle(currentCost, creditsBalance) : null)
	const isDisabled =
		(!prompt.trim() && promptRequired) ||
		namedSlotsMissing ||
		userValueMissing ||
		isLoading ||
		!user ||
		isUploading ||
		// Факт-проверка против гонки isUploading: при параллельных загрузках
		// первая завершившаяся сбрасывает флаг, пока вторая ещё грузится —
		// кнопка «Создать» не должна оживать, пока есть итемы без url.
		hasPendingUploads ||
		(requiresImage && !uploadedImage?.url) ||
		(requiresTwoImages && (!uploadedImage?.url || !swapImage?.url)) ||
		// HeyGen translate ставит requiresVideo=true (чтобы UploadThumb появился),
		// но valid также если юзер вставил YouTube/Drive URL в HeyGenTranslateUrlField.
		// Для остальных video-моделей URL не bypass'ит — только uploadedVideo.
		(requiresVideo && !uploadedVideo?.url && !settings.heygenVideoUrl) ||
		addVocalsBlocked ||
		isAudioOverLimit ||
		// Сцена не подходит модели (i2v «первый кадр») — не даём запустить
		// заведомо не то. Причину и «Убрать сцену» показывает SceneNotice.
		!!scene?.blocked ||
		costHold

	// speedOptions / versionOptions / safetyOptions / formatOptions /
	// orientationOptions / videoRefTypeOptions — все перенесены в
	// SettingsPillsRow.tsx (там они и нужны для рендера соответствующих
	// pill'ов). Здесь они больше не используются после делегирования
	// pill-рендера в shared компонент.

	// ── Style hint portal ─────────────────────────────────────
	// «Больше не показывать» (eye-off слева на pill): флаг стоит → подсказка не
	// рендерится и не считается. Флаг per-браузер в localStorage (как туры).
	const [hintHidden, setHintHidden] = useState(() => {
		try { return localStorage.getItem(HINT_HIDDEN_KEY) === '1' } catch { return false }
	})
	const uploadsRef = useRef<HTMLDivElement>(null)
	const mobileUploadsRef = useRef<HTMLDivElement>(null)
	const hintDivRef = useRef<HTMLDivElement>(null)
	const [hintPos, setHintPos] = useState<{
		bottom: number
		left: number
		rawLeft: number
		arrowLeft: number
		visible: boolean
	} | null>(null)

	// Позиция зелёной подсказки «Добавьте фото» — от прямоугольника поля
	// загрузки. В useCallback, чтобы пересчитывать не только при показе, но и на
	// РЕСАЙЗЕ виджета/окна (иначе пилюля «отклеивалась» от поля — см. эффект ниже).
	const computeHintPos = useCallback(() => {
		const desktopEl = uploadsRef.current
		const mobileEl = mobileUploadsRef.current
		const desktopR = desktopEl?.getBoundingClientRect()
		const el = desktopR && desktopR.width > 0 ? desktopEl! : mobileEl
		if (!el) return
		const r = el.getBoundingClientRect()
		// Якорь невидим → ПРЯЧЕМ пилюлю (не оставляем висеть с протухшей
		// позицией). Два случая: (а) сам якорь схлопнут; (б) виджет СВЁРНУТ —
		// тело клипается grid-row 0fr + overflow:hidden, при этом rect якоря
		// остаётся полноразмерным, поэтому сверяемся с рамкой клипа-тела.
		if (r.width < 8 || r.height < 8) {
			setHintPos(null)
			return
		}
		const clip = el.closest('.gw__input-col__body')
		if (clip) {
			const cb = clip.getBoundingClientRect()
			if (cb.height < 8 || r.top > cb.bottom - 4) {
				setHintPos(null)
				return
			}
		}
		const firstSlot = el.querySelector<HTMLElement>('.gm-upload-slot, .gm-multi-row')
		const slotRect = firstSlot ? firstSlot.getBoundingClientRect() : r
		const centerX = slotRect.left + slotRect.width / 2
		const bottom = window.innerHeight - r.top - 5
		// Ширина пилюли уже известна (после первого показа) → клампим к краям
		// экрана здесь же, без цикла hide→show (иначе на ресайзе бы мигало).
		const hintW = hintDivRef.current?.offsetWidth ?? 0
		if (hintW > 0) {
			const minLeft = hintW / 2 + 8
			const maxLeft = window.innerWidth - hintW / 2 - 8
			const clampedLeft = Math.max(minLeft, Math.min(centerX, maxLeft))
			setHintPos({ bottom, left: clampedLeft, rawLeft: centerX, arrowLeft: hintW / 2 + (centerX - clampedLeft), visible: true })
		} else {
			// Первый показ: ширина ещё не измерена → второй эффект измерит и покажет.
			setHintPos({ bottom, left: centerX, rawLeft: centerX, arrowLeft: 50, visible: false })
		}
	}, [])

	useLayoutEffect(() => {
		if (!showStyleHint || hintHidden) {
			setHintPos(null)
			return
		}
		const timer = setTimeout(computeHintPos, 380)
		// Сворачивание виджета НЕ ресайзит окно, но схлопывает ЯКОРЬ (grid-row
		// 0fr) — наблюдаем размер якоря: guard в computeHintPos спрячет пилюлю
		// у свёрнутого виджета и вернёт при раскрытии.
		const ro = new ResizeObserver(() => computeHintPos())
		if (uploadsRef.current) ro.observe(uploadsRef.current)
		if (mobileUploadsRef.current) ro.observe(mobileUploadsRef.current)
		// Клипающее тело виджета (его высота и схлопывается при сворачивании).
		const clipEl = uploadsRef.current?.closest('.gw__input-col__body')
		if (clipEl) ro.observe(clipEl)
		return () => {
			clearTimeout(timer)
			ro.disconnect()
		}
	}, [showStyleHint, hintHidden, computeHintPos])

	useLayoutEffect(() => {
		if (!hintPos || hintPos.visible || !hintDivRef.current) return
		const hintW = hintDivRef.current.offsetWidth
		const minLeft = hintW / 2 + 8
		const maxLeft = window.innerWidth - hintW / 2 - 8
		const clampedLeft = Math.max(minLeft, Math.min(hintPos.rawLeft, maxLeft))
		const arrowLeft = hintW / 2 + (hintPos.rawLeft - clampedLeft)
		setHintPos((p) => (p ? { ...p, left: clampedLeft, arrowLeft, visible: true } : null))
	}, [hintPos])

	useEffect(() => {
		if (showStyleHint && (uploadedImage || referenceImages.length > 0)) {
			setShowStyleHint(false)
		}
	}, [uploadedImage, referenceImages.length]) // eslint-disable-line react-hooks/exhaustive-deps

	// Close-only dismiss (без localStorage). Hint появится снова при
	// следующем триггере (apply style card / requiresImage модель и т.д.)
	// — юзер фидбек: не нужен permanent dismiss, hint полезен напоминанием.
	const handleDismissHint = useCallback(() => {
		setShowStyleHint(false)
	}, [setShowStyleHint])

	// «Больше не показывать»: ставим флаг → подсказка не появится никогда.
	const handleHideHintForever = useCallback(() => {
		try { localStorage.setItem(HINT_HIDDEN_KEY, '1') } catch { /* quota — не критично */ }
		setHintHidden(true)
		setShowStyleHint(false)
	}, [setShowStyleHint])

	const uploadHintPortal =
		showStyleHint && !hintHidden && hintPos && !tourActive
			? createPortal(
					<div
						ref={hintDivRef}
						className="gm-upload-hint"
						style={{
							position: 'fixed',
							bottom: hintPos.bottom,
							left: hintPos.left,
							visibility: hintPos.visible ? 'visible' : 'hidden',
							['--hint-arrow-left' as string]: hintPos.arrowLeft + 'px',
						}}
					>
						{t('generate.upload.addHintText').split('\n').map((l: string, i: number) => <React.Fragment key={i}>{i > 0 && <br />}{l}</React.Fragment>)}
						<button
							className="gm-upload-hint__forget"
							onClick={handleHideHintForever}
							type="button"
							aria-label="Больше не показывать подсказку"
							title="Больше не показывать"
						>
							<Icon id="gen-eye-off" size={10} />
						</button>
						<button
							className="gm-upload-hint__close"
							onClick={handleDismissHint}
							type="button"
							aria-label={t('generate.upload.dismissHintLabel')}
						>
							<Icon id="gen-close" size={10} />
						</button>
					</div>,
					document.body,
				)
			: null

	// ── Resize handle ────────────────────────────────────────
	const gmRef = useRef<HTMLDivElement>(null)
	const [gmHeight, setGmHeight] = useState<number | null>(null)
	const dragStartY = useRef(0)
	const dragStartH = useRef(0)

	const handleResizeStart = useCallback((e: React.PointerEvent) => {
		e.preventDefault()
		const el = gmRef.current
		if (!el) return

		dragStartY.current = e.clientY
		dragStartH.current = el.offsetHeight

		const textareaH = el.querySelector<HTMLElement>('.gm__textarea')?.offsetHeight ?? 68
		const minH = dragStartH.current - textareaH + 32

		// Pointer Events — единый путь для мыши И тача (mouse-only ручка не тянулась
		// пальцем). touch-action:none на ручке (CSS) глушит нативный скролл жеста.
		const onMove = (e: PointerEvent) => {
			const dy = dragStartY.current - e.clientY
			setGmHeight(Math.min(700, Math.max(minH, dragStartH.current + dy)))
		}
		const onUp = () => {
			window.removeEventListener('pointermove', onMove)
			window.removeEventListener('pointerup', onUp)
			window.removeEventListener('pointercancel', onUp)
		}
		window.addEventListener('pointermove', onMove)
		window.addEventListener('pointerup', onUp)
		window.addEventListener('pointercancel', onUp)
	}, [])

	// Зелёная подсказка «Добавьте фото» едет за полем загрузки при ресайзе
	// виджета (ручкой → gmHeight → двигается ряд загрузки / растёт textarea) и
	// при ресайзе окна. Первый (initial) фейр ResizeObserver пропускаем —
	// начальную позицию ставит 380ms-таймаут выше (после slide-up анимации).
	useLayoutEffect(() => {
		if (!showStyleHint || hintHidden) return
		let first = true
		const ro = new ResizeObserver(() => {
			if (first) { first = false; return }
			computeHintPos()
		})
		if (gmRef.current) ro.observe(gmRef.current)
		window.addEventListener('resize', computeHintPos)
		return () => {
			ro.disconnect()
			window.removeEventListener('resize', computeHintPos)
		}
	}, [showStyleHint, hintHidden, computeHintPos])

	if (!model) return null

	// «Придумать идею / Улучшить промпт» — ОДНА реализация на обе раскладки
	// (решение владельца 2026-08-01: мобильный вариант удачнее десктопного, в
	// шапке кнопки-дубля больше нет). Контекстна: пустое поле открывает
	// Идея-мастер, набранный текст — улучшает промпт. Ряды взаимоисключающие
	// (.gm__bottom скрыт ≤768px, .gm__mobile-bar показан только там), поэтому
	// вставка в оба места даёт одну живую кнопку. На десктопе рядом с иконкой
	// стоит подпись ТЕКУЩЕГО действия — иначе «Придумать идею» врала бы на
	// наборе текста, где кнопка улучшает промпт.
	// Аудио и презентации живут без «Придумать идею / Улучшить промпт»
	// (владелец 2026-08-18): у музыки свои креатив-инструменты, у Gamma тему
	// задают её собственные поля. Кнопку не гасим, а НЕ РЕНДЕРИМ — гашёная
	// кнопка всё равно занимает место в тулбаре и читается как «сломалось».
	const ideaBtnAvailable = !isAudio && !isPresentation
	const ideaWizardMode = !autoPrompt.isActive && !!onIdeaWizard
	const ideaLabel = ideaWizardMode ? t('generate.ideaButton') : t('generate.input.enhancePromptShort')
	// Только иконка, кружок в стекле «Повторить движение» (владелец 23.10): на компьютере — внутри поля ввода справа,
	// на телефоне — в мобильном ряду; что делает кнопка сейчас (идея или улучшить промпт), говорит подсказка.
	// Подсказка — общий SideTooltip сайта (владелец: «везде тултипы при наведении»), а не системный title браузера.
	const ideaActionBtn = () => (
		<SideTooltip text={ideaWizardMode ? 'Придумать идею: мастер подскажет, что сгенерировать' : 'Улучшить промпт: ИИ допишет детали к вашему тексту'} preferSide="left">
			<button
				type="button"
				className={`gm__mobile-action-btn gm__mobile-auto-prompt gm__idea-glass${(autoPrompt.isActive || !!onIdeaWizard) ? ' gm__mobile-auto-prompt--active' : ''}${autoPrompt.state === 'loading' ? ' gm__mobile-auto-prompt--loading' : ''}${autoPrompt.state === 'animating' ? ' gm__mobile-auto-prompt--animating' : ''}`}
				onClick={(e) => {
					if (ideaWizardMode) {
						onIdeaWizard!(e)
						return
					}
					autoPrompt.run()
				}}
				disabled={(!autoPrompt.isActive && !onIdeaWizard) || autoPrompt.state !== 'idle' || isLoading}
				aria-label={ideaLabel}
			>
				<Icon id="gen-auto-prompt" size={18} />
			</button>
		</SideTooltip>
	)
	// «Повторить движение» (владелец 23.10): пилюля режима сразу за «Придумать идею», в обеих раскладках — одна
	// реализация, как у кнопки идеи. Подпись на десктопе, на узком экране только фигурка с подсказкой.
	const motionActionBtn = (labeled: boolean) => onMotionTool ? (
		<SideTooltip text="Повторить движение: ваш аватар или фото повторит движение из ролика">
			<button
				type="button"
				className={`gm__mobile-action-btn gm__motion-btn${labeled ? ' gm__motion-btn--labeled' : ''}`}
				onClick={onMotionTool}
				disabled={isLoading}
				aria-label="Повторить движение"
			>
				<PersonStanding size={18} aria-hidden="true" />
				{labeled && <span className="gm__idea-label">Повторить движение</span>}
			</button>
		</SideTooltip>
	) : null

	// Кнопки демо-переменных — рендерятся в ДВУХ местах (десктоп: сбоку в ряду
	// .gm__uploads; мобилка: снизу отдельным рядом), вынесены сюда ради DRY.
	const variantButtons = variantChips.map((v) => (
		<button
			key={v.key}
			type="button"
			role="tab"
			aria-selected={v.key === activeVariantKey}
			className={`gm__variant${v.key === activeVariantKey ? ' gm__variant--on' : ''}`}
			onClick={() => (v.isOpen ? selectCustomVariant(v.key, getAttachedStyleCard()?.customValue ?? '') : selectVariant(v.key))}
			disabled={isLoading}
		>
			{v.label}
		</button>
	))

	// Карточное «поле юзера» мультистиль-карточки (владелец 2026-08-14) —
	// живёт рядом с чипами стилей в обоих местах их рендера; значение встаёт
	// на {value} патча выбранного стиля (VariantContext.setUserValue).
	// Показ зависит от выбранного стиля: {value} в его патче или в базе
	// (владелец 2026-08-16) — стили без плейсхолдера поле не показывают.
	const userValueField = activeUserInput && activeUsesValue ? (
		<span className="gm__uval">
			<span className="gm__uval-k">{activeUserInput.label || 'Значение'}</span>
			{activeUserInput.type === 'number' ? (
				<NumberStepper
					value={cardUserValue.trim() ? parseInt(cardUserValue, 10) : null}
					onChange={(v) => setUserValue(v == null ? '' : String(v))}
					min={activeUserInput.min ?? undefined}
					max={activeUserInput.max ?? undefined}
					disabled={isLoading}
					ariaLabel={activeUserInput.label || 'Число'}
				/>
			) : (
				<input
					className="gm__uval-input"
					type="text"
					maxLength={80}
					placeholder={activeUserInput.placeholder || 'Введите значение'}
					value={cardUserValue}
					onChange={(e) => setUserValue(e.target.value)}
					disabled={isLoading}
				/>
			)}
		</span>
	) : null

	return (
		<>
			{uploadHintPortal}
			{/* Каркас бара общий с доком ассетов (components/bar/BarShell): рамка .gm и колонка .gm__content.
			    modeNav переехал в нижний ряд (.gm__bottom) — пилюля-поповер Artlist-style вместо рейла слева. */}
			<BarShell className={gmHeight ? 'gm--resized' : undefined} ref={gmRef} style={gmHeight ? { height: `${gmHeight}px` } : undefined}>
					{/* ── Resize handle ── */}
					<div className="gm__resize-handle" onPointerDown={handleResizeStart}>
						<svg
							width="14"
							height="14"
							viewBox="0 0 14 14"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<line
								x1="3"
								y1="11"
								x2="11"
								y2="3"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
							/>
							<line
								x1="7"
								y1="11"
								x2="11"
								y2="7"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
							/>
						</svg>
					</div>

					{/* Слот виджета первым видимым элементом карточки — строка-пин
					    «Ваш стиль» живёт на поверхности .gm, не в зазоре над ней. */}
					{topSlot}

					{/* ── Presentation controls (placeholder + info-card +
					    create-from-scratch / edit toggle) — standalone, без wrapper.
					    UploadsRow для isPresentation возвращает PresentationControls. */}
					{isPresentation && <UploadsRow onOpenResult={onOpenResult} />}

					{/* HeyGen Video Translate: модель имеет requiresVideo=true в
					    MODEL_METADATA → стандартный UploadThumb для mp4 рендерится
					    автоматически в Upload row ниже. Этот mini-block — доп. text
					    input для тех кто хочет вставить YouTube/Drive URL без
					    скачивания видео. URL приоритетнее uploadedVideo на backend'е. */}
					{getHeyGenModelKind(model.backendModel) === 'video-translate' && (
						<HeyGenTranslateUrlField
							settings={settings}
							updateSetting={updateSetting}
							disabled={isLoading}
						/>
					)}

					{/* ── Upload row (audio header / image / video uploads) ──
					    Wrapper .gm__uploads даёт layout (flex + gap + border-bottom).
					    Для audio добавляется --audio modifier (space-between для
					    title-wrap слева + result-slot справа). UploadsRow внутри
					    рендерит: audio header (Suno title+lyrics или sounds-params)
					    либо upload-thumbs. Single source of truth с home/bots/*. */}
					{isAudio && (opAvailable('soundtrack', providerOf(model?.id)) || opAvailable('tts', providerOf(model?.id))) && audioOperation === 'generate' && settings.audioSubmode !== 'sounds' && (
						<div className="gm__audio-submode">
							<button type="button" className={`gm__audio-submode-pill${settings.audioSubmode !== 'soundtrack' && settings.audioSubmode !== 'tts' ? ' gm__audio-submode-pill--active' : ''}`} onClick={() => updateSetting('audioSubmode', 'music')} disabled={isLoading}>Песня</button>
							{opAvailable('soundtrack', providerOf(model?.id)) && (
								<SideTooltip text="Mureka улавливает настроение фото или видео и создаёт под него музыку. Может выйти мелодия (инструментал) или песня с вокалом — по умолчанию выбирает сам визуал. Чтобы задать явно, впиши в подсказку «инструментал» или «с вокалом». Готовый трек попадёт в ленту: его можно продлевать, перепевать, делать дорожки и т.д.">
									<button type="button" className={`gm__audio-submode-pill${settings.audioSubmode === 'soundtrack' ? ' gm__audio-submode-pill--active' : ''}`} onClick={() => updateSetting('audioSubmode', 'soundtrack')} disabled={isLoading}>Из фото/видео<span className="gm__audio-submode-q" aria-hidden="true">?</span></button>
								</SideTooltip>
							)}
							{/* Озвучку делает своя модель (ElevenLabs): у музыкальной кнопка ведёт на
							    вкладку «Озвучка», там виджет сам переключит модель и режим. */}
							<button type="button" className={`gm__audio-submode-pill${settings.audioSubmode === 'tts' ? ' gm__audio-submode-pill--active' : ''}`} onClick={() => (supportsTts(model?.id) ? updateSetting('audioSubmode', 'tts') : navigate(TTS_ROUTE))} disabled={isLoading}>Озвучка<span className="beta-badge">BETA</span></button>
						</div>
					)}
					{!isPresentation && (isAudio
						? true
						: showImageUpload ||
							requiresVideo ||
							model.supportsReferenceVideo ||
							model.supportsEndImage ||
							!!appendUploads) && (
						<BarUploads className={isAudio ? 'gm__uploads--audio' : undefined} innerRef={uploadsRef}>
							{/* Раскладка «только фото» привязана к МОДЕЛИ, а не к режиму:
							    нейронка без промпта с двумя снимками получает именованные
							    слоты и в обычном баре, и в карточке. Остальные модели
							    идут прежней веткой — их логику не трогаем. */}
							<UploadsRow makeShowPicker={makeShowPicker} appendSlot={appendUploads} hint={hint} faceSwapLayout={modelIgnoresPrompt && requiresTwoImages} namedSlots={attachedCard?.photo_slots} activeSlotNums={activePhotoSlotNums} hideCharacter={attachedCard?.hide_character} photoLimit={attachedCard?.user_photo_limit} photoHint={attachedCard?.user_photo_hint} hideEndImage={attachedCard?.hide_end_image} endImageHint={attachedCard?.end_image_hint} characterHint={attachedCard?.character_hint} hideVideoSlot={!!attachedCard?.userVideoLocked} hideAudioSlot={!!attachedCard?.userAudioLocked} />
							{/* Десктоп-картинка: карточка-пин + чипы вариаций правым блоком
							    того же ряда иконок. .gm__uploads скрыт на мобиле → блок сам
							    desktop-only. Порядок [чипы][карточка]: карточка крайняя справа. */}
							{!isAudio && (variantChips.length > 0 || styleCardSlot) && (
								<div className="gm__uploads-aside">
									{user && variantChips.length > 0 && (
										<div className="gm__variants gm__variants--inrow" role="tablist" aria-label="Варианты карточки">{variantButtons}{userValueField}</div>
									)}
									{styleCardSlot}
								</div>
							)}
						</BarUploads>
					)}

					{/* Демо-переменные для МОБИЛЫ — отдельным рядом над промптом (ряд
					    иконок там скрыт). На десктопе этот ряд скрыт (см. --under) —
					    чипы там живут в .gm__uploads-aside рядом с карточкой. */}
					{user && variantChips.length > 0 && (
						<div className="gm__variants gm__variants--under" role="tablist" aria-label="Варианты карточки">{variantButtons}{userValueField}</div>
					)}

					{/* Строка-статус сцены (предупреждение/барьер) — над промптом,
					    видна на десктопе и мобиле без ховера. */}
					<SceneNotice />
					<MotionRiskNotice />

					{/* ── Prompt textarea ── */}
					<BarPrompt className={modelIgnoresPrompt ? 'gm__prompt--none' : undefined}>
						{modelIgnoresPrompt && (
							<p className="gm__prompt-none">
								{isVideoReplaceModel(model)
									? 'Этой нейросети текст не нужен — аватар с фото встанет на место человека в ролике. Загрузите фото и ролик выше.'
									: 'Этой нейросети текст не нужен — она переносит лицо с одного фото на другое. Загрузите оба фото выше.'}
							</p>
						)}
						<PromptPillEditor
							ref={pillEditorRef}
							className="gm__textarea gm__pill-editor"
							placeholder={
								// Контекстные плейсхолдеры — default "Опишите что хотите
								// сгенерировать" бесполезен когда поле имеет специфическую
								// семантику (текст песни / инструкция к PPTX / описание
								// звукового эффекта).
								isPresentation
									? (settings.presentation?.mode === 'create'
										? 'Тема презентации: История искусственного интеллекта / Бизнес-план открытия кофейни / Основы React...'
										: 'Инструкция: Переведи на английский / Сократи до 3 пунктов / Сделай формальнее...')
									: isAudio
									? audioPromptPlaceholder(settings.audioSubmode, audioOperation)
									: (paramHints.prompt?.description || `${t('generate.input.placeholder')}${sceneTools && !attachedCard?.hide_character ? ' Через @ — аватары, локации, предметы' : ''}`)
							}
							value={prompt}
							onChange={onPromptEdit}
							disabled={isLoading}
							slots={pillSlots}
							// «@» — меню персонажей, локаций и предметов (свои и из библиотеки). Гостю, аудио и презентациям —
							// просто символ; карточка с hide_character отключает сцену целиком — и здесь тоже.
							onMention={sceneTools && !isAudio && !isPresentation && !modelIgnoresPrompt && !attachedCard?.hide_character ? setMention : undefined}
							onMentionKey={(k) => mentionMenuRef.current?.key(k) ?? false}
							// Hard-лимит для Suno (динамический по customMode) — редактор
							// блокирует и typing, и paste-overflow, как раньше textarea.
							{...(audioPromptLimit !== null && { maxLength: audioPromptLimit })}
						/>
						{mention && (
							<SceneMentionMenu
								query={mention}
								handleRef={mentionMenuRef}
								onClose={() => setMention(null)}
								onAssets={(list) =>
									setMentionAssets((prev) => ({
										...prev,
										...Object.fromEntries(list.map((a) => [a.id, { name: a.name, url: assetThumb(a), kind: a.kind }])),
									}))
								}
								onPick={(a) => {
									setMentionAssets((prev) => ({ ...prev, [a.id]: { name: a.name, url: assetThumb(a), kind: a.kind } }))
									// Чип в текст; ассет сам встанет в сцену — useSceneApply видит новое упоминание.
									pillEditorRef.current?.insertMention(assetToken(a.id))
									setMention(null)
								}}
							/>
						)}
						{/* «Сослаться на фото»: вставка пилюли в позицию курсора — удобно
						    писать свой промпт, опираясь на конкретное фото. Фразы есть
						    только для первых четырёх референсов. */}
						{!modelIgnoresPrompt && !promptLocked && !isAudio && !isPresentation && insertChips.length > 0 && (
							<PillInsertChips
								chips={(() => {
									// Файл приложен, а промпт о нём молчит — модель решит сама (utils/pillMentions).
									const unmentioned = unmentionedChipIds(prompt, insertChips)
									return insertChips.map((c) => ({
										...c,
										unmentioned: unmentioned.has(c.id),
										onInsert: () => { if (c.phrase) pillEditorRef.current?.insertText(` ${c.phrase} `) },
									}))
								})()}
							/>
						)}
						{/* Гость переиспользовал готовый промпт (применил карточку/стиль) →
						    поле ввода закрыто блюром + кнопки входа, ввод заблокирован.
						    Снимается при очистке виджета (пин отцепляется → promptLocked=false). */}
						{promptLocked && (
							<div className="gm__prompt-locked">
								<Icon id="gen-lock" size={20} className="gm__prompt-locked__icon" />
								<span className="gm__prompt-locked__actions">
									<a {...authLink('/login')} className="gm__prompt-locked__btn gm__prompt-locked__btn--ghost">Войти</a>
									<a {...authLink('/register')} className="gm__prompt-locked__btn">Регистрация</a>
								</span>
							</div>
						)}
						{/* Счётчик символов для Suno music. В обычном режиме API режет
						    prompt до 200, в кастомном — до 5000 (полная лирика).
						    Показываем юзеру до отправки чтобы не получить ошибку API,
						    и подсказываем включить кастомный режим. Submode 'sounds'
						    идёт через отдельный endpoint и под этот лимит не попадает. */}
						{isAudio && settings.audioSubmode !== 'sounds' && (() => {
							const limit = settings.audioSubmode === 'tts' ? 500 : settings.audioSubmode === 'soundtrack' ? 1024 : settings.audioCustomMode ? 5000 : 200
							const isOver = prompt.length > limit
							return (
								<div className={`gm__prompt-counter${isOver ? ' gm__prompt-counter--over' : ''}`}>
									<span className="gm__prompt-counter-num">{prompt.length}/{limit}</span>
									{isOver && (
										<span className="gm__prompt-counter-msg">
											{settings.audioCustomMode
												? 'Промпт не может превышать 5000 символов'
												: 'Промпт превышает 200 символов. Включите режим «Кастомный» для ввода полного текста песни'}
										</span>
									)}
								</div>
							)
						})()}
						{/* SunoLyricsButton перенесён наверх — в .gm__audio-title-wrap рядом
						    с полем «Название трека». Logically связанные поля вместе. */}
						{/* «Придумать идею / Улучшить промпт» на компьютере — внутри поля ввода, справа (владелец 23.10);
						    телефон — та же кнопка в мобильном ряду. Обёртка скрыта ≤768px, ряд — наоборот. */}
						{ideaBtnAvailable && <div className="gm__idea-infield">{ideaActionBtn()}</div>}
					</BarPrompt>

					{/* ── Negative prompt ── */}
					<div className={`gm__neg ${negativePromptOpen ? 'gm__neg--open' : ''}`}>
						<div className="gm__neg-inner">
							<span className="gm__neg-label">{t('generate.input.negativePrompt')}</span>
							<textarea
								ref={negRef}
								className="gm__neg-textarea"
								placeholder={t('generate.input.negativePlaceholder')}
								rows={2}
								value={settings.negativePrompt ?? ''}
								onChange={(e) => updateSetting('negativePrompt', e.target.value)}
								disabled={isLoading}
							/>
						</div>
					</div>

					{/* ── Error ── */}
					{error && (
						<div className="gm__error">
							<svg
								width="14"
								height="14"
								viewBox="0 0 24 24"
								fill="currentColor"
								aria-hidden="true"
								style={{ flexShrink: 0 }}
							>
								<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
							</svg>
							<span className="gm__error-text">
								{error.includes('No response from MidJourney')
									? t('generate.input.errorMidjourney')
									: error.includes('Генерация не удалась')
										? t('generate.input.errorFailed')
										: error.includes('Not authenticated')
											? t('generate.input.errorAuth')
											: error}
							</span>
							<button
								type="button"
								className="gm__error-close"
								onClick={clearError}
								aria-label={t('generate.input.closeError')}
							>
								<svg
									width="12"
									height="12"
									viewBox="0 0 16 16"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
								>
									<path d="M2 2l12 12M14 2L2 14" strokeLinecap="round" />
								</svg>
							</button>
						</div>
					)}

					{/* ── Mobile bottom bar ── */}
					<BarMobileBar>
						{/* Presentation: загрузка PPTX/шаблона/картинок живёт в
						    <PresentationControls /> (рендерится выше). Стандартный
						    мобильный upload-ряд тут не нужен. */}
						{!isPresentation && <div className="gm__mobile-uploads" ref={mobileUploadsRef}>
							{showImageUpload &&
								(supportsMultipleImages && !requiresImage && !requiresTwoImages ? (
									<MultiImgRow
										images={referenceImages}
										maxImages={maxImages}
										isLoading={isLoading}
										isUploading={isUploading}
										inputRef={fileInputRef}
										onUpload={handleMultipleImageUpload}
										onRemove={(idx) =>
											setReferenceImages((prev) => prev.filter((_, i) => i !== idx))
										}
										onShowPicker={makeShowPicker('images', fileInputRef)}
										onHintDismiss={() => setShowStyleHint(false)}
									/>
								) : (
									<UploadThumb
										file={uploadedImage}
										isUploading={isUploading}
										isLoading={isLoading}
										accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
										inputRef={fileInputRef}
										onChange={handleImageUpload}
										onRemove={() => {
											setUploadedImage(null)
											if (fileInputRef.current) fileInputRef.current.value = ''
										}}
										icon={<Icon id="gen-add" size={20} />}
										label={
											// Порядок слотов у замены лица смыслонесущий: первый —
											// целевая картинка, а не «ваше фото». Без явных подписей
											// человек грузит наоборот и получает не тот результат.
											requiresTwoImages
												? t('generate.upload.faceSwapTarget')
												: t('generate.upload.addImage')
										}
										required={requiresImage || requiresTwoImages}
										onHintDismiss={() => setShowStyleHint(false)}
										onShowPicker={makeShowPicker('images', fileInputRef)}
									/>
								))}
							{requiresVideo && (
								<UploadThumb
									file={uploadedVideo}
									isUploading={isUploading}
									isLoading={isLoading}
									accept="video/mp4,video/quicktime,video/webm"
									inputRef={videoInputRef}
									onChange={handleVideoUpload}
									onRemove={() => setUploadedVideo(null)}
									icon={<Icon id="gen-video" size={20} />}
									label={t('generate.upload.motionVideo')}
									required
								/>
							)}
							{requiresTwoImages && (
								<UploadThumb
									file={swapImage}
									isUploading={isUploading}
									isLoading={isLoading}
									accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
									inputRef={swapInputRef}
									onChange={handleSwapImageUpload}
									onRemove={() => setSwapImage(null)}
									icon={<Icon id="gen-swap" size={20} />}
									label={t('generate.upload.faceSwapSource')}
									required
								/>
							)}
							<button
								type="button"
								className="gm__mobile-action-btn gm__mobile-model-btn"
								onClick={() => setMobileSheetOpen(true)}
								aria-label={t('generate.input.settingsAndModel')}
							>
								<svg
									width="18"
									height="18"
									viewBox="0 0 24 24"
									fill="currentColor"
									aria-hidden="true"
								>
									<path d="M14.757 18.5484C14.4186 18.5484 14.1085 18.4638 13.8265 18.2946C13.5496 18.1306 13.3266 17.9075 13.1574 17.6255C12.9882 17.3487 12.9036 17.0411 12.9036 16.7027C12.9036 16.3643 12.9882 16.0567 13.1574 15.7798C13.3266 15.4979 13.5496 15.2748 13.8265 15.1108C14.1085 14.9416 14.4186 14.857 14.757 14.857C15.0954 14.857 15.403 14.9416 15.6799 15.1108C15.9619 15.2748 16.1849 15.4979 16.3489 15.7798C16.5181 16.0567 16.6027 16.3643 16.6027 16.7027C16.6027 17.0411 16.5181 17.3487 16.3489 17.6255C16.1849 17.9075 15.9619 18.1306 15.6799 18.2946C15.403 18.4638 15.0954 18.5484 14.757 18.5484ZM14.757 17.6025C15.0082 17.6025 15.221 17.5153 15.3953 17.341C15.5696 17.1667 15.6568 16.9539 15.6568 16.7027C15.6568 16.4464 15.5696 16.2336 15.3953 16.0644C15.221 15.8901 15.0082 15.8029 14.757 15.8029C14.5058 15.8029 14.293 15.8901 14.1187 16.0644C13.9444 16.2336 13.8572 16.4464 13.8572 16.7027C13.8572 16.9539 13.9444 17.1667 14.1187 17.341C14.293 17.5153 14.5058 17.6025 14.757 17.6025ZM13.5342 16.0798V17.3179H4.86713C4.69794 17.3179 4.55182 17.259 4.42877 17.1411C4.31085 17.018 4.25189 16.8719 4.25189 16.7027C4.25189 16.5284 4.31085 16.3823 4.42877 16.2643C4.55182 16.1413 4.69794 16.0798 4.86713 16.0798H13.5342ZM19.1559 16.0798C19.3149 16.0798 19.4533 16.1413 19.5712 16.2643C19.6891 16.3823 19.7481 16.5284 19.7481 16.7027C19.7481 16.8719 19.6891 17.018 19.5712 17.1411C19.4533 17.259 19.3149 17.3179 19.1559 17.3179H16.0952V16.0798H19.1559ZM9.32758 13.8572C8.9892 13.8572 8.67902 13.7726 8.39703 13.6035C8.12018 13.4343 7.89716 13.2112 7.72797 12.9344C7.5639 12.6524 7.48187 12.3422 7.48187 12.0038C7.48187 11.6655 7.5639 11.3578 7.72797 11.081C7.89716 10.799 8.12018 10.576 8.39703 10.4119C8.67902 10.2427 8.9892 10.1581 9.32758 10.1581C9.66595 10.1581 9.97357 10.2427 10.2504 10.4119C10.5324 10.576 10.7554 10.799 10.9195 11.081C11.0887 11.3578 11.1733 11.6655 11.1733 12.0038C11.1733 12.3422 11.0887 12.6524 10.9195 12.9344C10.7554 13.2112 10.5324 13.4343 10.2504 13.6035C9.97357 13.7726 9.66595 13.8572 9.32758 13.8572ZM9.32758 12.9036C9.58392 12.9036 9.79669 12.8165 9.96588 12.6422C10.1402 12.4678 10.2274 12.2551 10.2274 12.0038C10.2274 11.7475 10.1402 11.5347 9.96588 11.3655C9.79669 11.1912 9.58392 11.1041 9.32758 11.1041C9.07635 11.1041 8.86359 11.1912 8.68927 11.3655C8.51495 11.5347 8.4278 11.7475 8.4278 12.0038C8.4278 12.2551 8.51495 12.4678 8.68927 12.6422C8.86359 12.8165 9.07635 12.9036 9.32758 12.9036ZM4.83636 11.3809H8.00482V12.6191H4.83636C4.68256 12.6191 4.54669 12.5601 4.42877 12.4422C4.31085 12.3243 4.25189 12.1782 4.25189 12.0038C4.25189 11.8295 4.31085 11.6834 4.42877 11.5655C4.54669 11.4424 4.68256 11.3809 4.83636 11.3809ZM19.1252 11.3809C19.2995 11.3809 19.4456 11.4424 19.5635 11.5655C19.6866 11.6834 19.7481 11.8295 19.7481 12.0038C19.7481 12.1782 19.6866 12.3243 19.5635 12.4422C19.4456 12.5601 19.2995 12.6191 19.1252 12.6191H10.5504V11.3809H19.1252ZM14.757 9.1507C14.4186 9.1507 14.1085 9.06866 13.8265 8.9046C13.5496 8.73541 13.3266 8.51239 13.1574 8.23553C12.9882 7.95355 12.9036 7.64337 12.9036 7.30499C12.9036 6.96661 12.9882 6.659 13.1574 6.38214C13.3266 6.10016 13.5496 5.87457 13.8265 5.70538C14.1085 5.53619 14.4186 5.4516 14.757 5.4516C15.0954 5.4516 15.403 5.53619 15.6799 5.70538C15.9619 5.87457 16.1849 6.10016 16.3489 6.38214C16.5181 6.659 16.6027 6.96661 16.6027 7.30499C16.6027 7.64337 16.5181 7.95355 16.3489 8.23553C16.1849 8.51239 15.9619 8.73541 15.6799 8.9046C15.403 9.06866 15.0954 9.1507 14.757 9.1507ZM14.757 8.20477C15.0082 8.20477 15.221 8.11761 15.3953 7.9433C15.5696 7.76385 15.6568 7.54852 15.6568 7.2973C15.6568 7.04608 15.5696 6.83331 15.3953 6.659C15.221 6.48468 15.0082 6.39752 14.757 6.39752C14.5058 6.39752 14.293 6.48468 14.1187 6.659C13.9444 6.83331 13.8572 7.04608 13.8572 7.2973C13.8572 7.54852 13.9444 7.76385 14.1187 7.9433C14.293 8.11761 14.5058 8.20477 14.757 8.20477ZM13.5727 6.68976V7.92792H4.86713C4.69794 7.92792 4.55182 7.86896 4.42877 7.75104C4.31085 7.62799 4.25189 7.47931 4.25189 7.30499C4.25189 7.1358 4.31085 6.99225 4.42877 6.87433C4.55182 6.75128 4.69794 6.68976 4.86713 6.68976H13.5727ZM19.1559 6.68976C19.3149 6.68976 19.4533 6.75128 19.5712 6.87433C19.6891 6.99225 19.7481 7.1358 19.7481 7.30499C19.7481 7.47931 19.6891 7.62799 19.5712 7.75104C19.4533 7.86896 19.3149 7.92792 19.1559 7.92792H15.9875V6.68976H19.1559Z" />
								</svg>
								<span className="gm__mobile-model-name">{model.shortName || model.name}</span>
							</button>
							{ideaBtnAvailable && ideaActionBtn()}
							{motionActionBtn(false)}
						</div>}
						{isPresentation && (
							<div className="gm__mobile-uploads">
								<button
									type="button"
									className="gm__mobile-action-btn gm__mobile-model-btn"
									onClick={() => setMobileSheetOpen(true)}
									aria-label={t('generate.input.settingsAndModel')}
								>
									<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
										<path d="M14.757 18.5484C14.4186 18.5484 14.1085 18.4638 13.8265 18.2946C13.5496 18.1306 13.3266 17.9075 13.1574 17.6255C12.9882 17.3487 12.9036 17.0411 12.9036 16.7027C12.9036 16.3643 12.9882 16.0567 13.1574 15.7798C13.3266 15.4979 13.5496 15.2748 13.8265 15.1108C14.1085 14.9416 14.4186 14.857 14.757 14.857C15.0954 14.857 15.403 14.9416 15.6799 15.1108C15.9619 15.2748 16.1849 15.4979 16.3489 15.7798C16.5181 16.0567 16.6027 16.3643 16.6027 16.7027C16.6027 17.0411 16.5181 17.3487 16.3489 17.6255C16.1849 17.9075 15.9619 18.1306 15.6799 18.2946C15.403 18.4638 15.0954 18.5484 14.757 18.5484ZM14.757 17.6025C15.0082 17.6025 15.221 17.5153 15.3953 17.341C15.5696 17.1667 15.6568 16.9539 15.6568 16.7027C15.6568 16.4464 15.5696 16.2336 15.3953 16.0644C15.221 15.8901 15.0082 15.8029 14.757 15.8029C14.5058 15.8029 14.293 15.8901 14.1187 16.0644C13.9444 16.2336 13.8572 16.4464 13.8572 16.7027C13.8572 16.9539 13.9444 17.1667 14.1187 17.341C14.293 17.5153 14.5058 17.6025 14.757 17.6025ZM13.5342 16.0798V17.3179H4.86713C4.69794 17.3179 4.55182 17.259 4.42877 17.1411C4.31085 17.018 4.25189 16.8719 4.25189 16.7027C4.25189 16.5284 4.31085 16.3823 4.42877 16.2643C4.55182 16.1413 4.69794 16.0798 4.86713 16.0798H13.5342ZM19.1559 16.0798C19.3149 16.0798 19.4533 16.1413 19.5712 16.2643C19.6891 16.3823 19.7481 16.5284 19.7481 16.7027C19.7481 16.8719 19.6891 17.018 19.5712 17.1411C19.4533 17.259 19.3149 17.3179 19.1559 17.3179H16.0952V16.0798H19.1559ZM9.32758 13.8572C8.9892 13.8572 8.67902 13.7726 8.39703 13.6035C8.12018 13.4343 7.89716 13.2112 7.72797 12.9344C7.5639 12.6524 7.48187 12.3422 7.48187 12.0038C7.48187 11.6655 7.5639 11.3578 7.72797 11.081C7.89716 10.799 8.12018 10.576 8.39703 10.4119C8.67902 10.2427 8.9892 10.1581 9.32758 10.1581C9.66595 10.1581 9.97357 10.2427 10.2504 10.4119C10.5324 10.576 10.7554 10.799 10.9195 11.081C11.0887 11.3578 11.1733 11.6655 11.1733 12.0038C11.1733 12.3422 11.0887 12.6524 10.9195 12.9344C10.7554 13.2112 10.5324 13.4343 10.2504 13.6035C9.97357 13.7726 9.66595 13.8572 9.32758 13.8572ZM9.32758 12.9036C9.58392 12.9036 9.79669 12.8165 9.96588 12.6422C10.1402 12.4678 10.2274 12.2551 10.2274 12.0038C10.2274 11.7475 10.1402 11.5347 9.96588 11.3655C9.79669 11.1912 9.58392 11.1041 9.32758 11.1041C9.07635 11.1041 8.86359 11.1912 8.68927 11.3655C8.51495 11.5347 8.4278 11.7475 8.4278 12.0038C8.4278 12.2551 8.51495 12.4678 8.68927 12.6422C8.86359 12.8165 9.07635 12.9036 9.32758 12.9036ZM4.83636 11.3809H8.00482V12.6191H4.83636C4.68256 12.6191 4.54669 12.5601 4.42877 12.4422C4.31085 12.3243 4.25189 12.1782 4.25189 12.0038C4.25189 11.8295 4.31085 11.6834 4.42877 11.5655C4.54669 11.4424 4.68256 11.3809 4.83636 11.3809ZM19.1252 11.3809C19.2995 11.3809 19.4456 11.4424 19.5635 11.5655C19.6866 11.6834 19.7481 11.8295 19.7481 12.0038C19.7481 12.1782 19.6866 12.3243 19.5635 12.4422C19.4456 12.5601 19.2995 12.6191 19.1252 12.6191H10.5504V11.3809H19.1252ZM14.757 9.1507C14.4186 9.1507 14.1085 9.06866 13.8265 8.9046C13.5496 8.73541 13.3266 8.51239 13.1574 8.23553C12.9882 7.95355 12.9036 7.64337 12.9036 7.30499C12.9036 6.96661 12.9882 6.659 13.1574 6.38214C13.3266 6.10016 13.5496 5.87457 13.8265 5.70538C14.1085 5.53619 14.4186 5.4516 14.757 5.4516C15.0954 5.4516 15.403 5.53619 15.6799 5.70538C15.9619 5.87457 16.1849 6.10016 16.3489 6.38214C16.5181 6.659 16.6027 6.96661 16.6027 7.30499C16.6027 7.64337 16.5181 7.95355 16.3489 8.23553C16.1849 8.51239 15.9619 8.73541 15.6799 8.9046C15.403 9.06866 15.0954 9.1507 14.757 9.1507ZM14.757 8.20477C15.0082 8.20477 15.221 8.11761 15.3953 7.9433C15.5696 7.76385 15.6568 7.54852 15.6568 7.2973C15.6568 7.04608 15.5696 6.83331 15.3953 6.659C15.221 6.48468 15.0082 6.39752 14.757 6.39752C14.5058 6.39752 14.293 6.48468 14.1187 6.659C13.9444 6.83331 13.8572 7.04608 13.8572 7.2973C13.8572 7.54852 13.9444 7.76385 14.1187 7.9433C14.293 8.11761 14.5058 8.20477 14.757 8.20477ZM13.5727 6.68976V7.92792H4.86713C4.69794 7.92792 4.55182 7.86896 4.42877 7.75104C4.31085 7.62799 4.25189 7.47931 4.25189 7.30499C4.25189 7.1358 4.31085 6.99225 4.42877 6.87433C4.55182 6.75128 4.69794 6.68976 4.86713 6.68976H13.5727ZM19.1559 6.68976C19.3149 6.68976 19.4533 6.75128 19.5712 6.87433C19.6891 6.99225 19.7481 7.1358 19.7481 7.30499C19.7481 7.47931 19.6891 7.62799 19.5712 7.75104C19.4533 7.86896 19.3149 7.92792 19.1559 7.92792H15.9875V6.68976H19.1559Z" />
									</svg>
									<span className="gm__mobile-model-name">{model.shortName || model.name}</span>
								</button>
							</div>
						)}
						{mobileModeNav}
						{isLoading && !onSendOverride && <DetachButton onClick={gen.detachGeneration} />}
						<CreateButton
							onClick={onSendOverride ?? handleGenerate}
							disabled={isDisabled || creditsShort}
							loading={isLoading}
							title={creditsTitle ?? `${currentCost} ${pluralizeTokens(currentCost)}`}
						/>
					</BarMobileBar>

					{/* Остаток бесплатного тира выбранной модели — единый индикатор
					    FreeTierBadge (виден и в компакт-виджете /bots/*, и в студии).
					    Рендерится только когда админ задал модели строку-лимита. */}
					{/* Гость гейтится: бесплатный тир существует только у юзера с
					    активным тарифом — без токена запрос был бы 401-шумом.
					    params РОВНО повторяют подписи money-path (images/video.py):
					    лишнее поле в подписи бейджа сматчило бы строку-лимит,
					    которую списание не увидит — бейдж врал бы «бесплатно». */}
					{creditsShort && <CreditsShortNote className="gm__credits-short" price={currentCost} balance={creditsBalance} />}
					{costIssue && <div className="gm__credits-short" role="status">{costIssue}</div>}
					{user && model && (model.category === 'image' || model.category === 'video') && (
						<FreeTierBadge className="gm__free-tier" data={freeTier} />
					)}

					{/* ── Desktop bottom bar ── */}
					<BarBottom>
						{modeNav}
						{(modeNav !== undefined || modelPickerCategory || showModelPicker) && (
							<WidgetModelPicker category={modelPickerCategory} />
						)}
						{/* Все pill'ы (Presentation/HeyGen/Audio image/video) — теперь
						    в shared SettingsPillsRow. Тот же компонент используется
						    на главной (HomeTemp). Новые pill'ы добавляются ОДИН РАЗ
						    в SettingsPillsRow.tsx и автоматом появляются на обоих
						    экранах — single source of truth, no drift. */}
						<div className="gm__pills">
							{/* Один поповер инструментов (Artlist-style) — общий компонент
							    с GenerateToolMode: summary-пилюля + секции. Одно-пилюльные
							    ветки рендерятся внутри как обычный ряд. */}
							<SettingsToolsPill />
							{/* Та же кнопка, что в мобильном ряду, — с подписью действия. */}
							{motionActionBtn(true)}
						</div>

						{isLoading && !onSendOverride && <DetachButton onClick={gen.detachGeneration} />}
						<button
							className={`gm__send ${isLoading ? 'gm__send--loading' : ''}`}
							onClick={onSendOverride ?? handleGenerate}
							disabled={isDisabled || creditsShort}
							aria-label={isLoading ? t('generate.input.generatingLabel') : t('generate.input.generateLabel')}
							title={creditsTitle ?? `${currentCost} ${pluralizeTokens(currentCost)}`}
						>
							{isLoading ? (
								<Icon id="gen-spin" size={18} className="spin" />
							) : (
								<>
									<Icon id="gen-send" size={16} />
									<span className="gm__send-label">{t('generate.input.create')}</span>
								</>
							)}
						</button>
					</BarBottom>

					{/* Подсказка «Войдите для генерации» убрана 2026-07-27: гостю уже
					    адресовано полноценное CTA (стекло с «Войдите, чтобы создать»
					    в tool-режиме, гостевые герои разделов), а строка под баром
					    дублировала его и на узких экранах наползала на панель. */}

					<MobileSettingsSheet
						open={mobileSheetOpen}
						onClose={() => setMobileSheetOpen(false)}
						model={model}
						settings={settings}
						isLoading={isLoading}
						selectedVideoDuration={selectedVideoDuration}
						uploadedVideoDuration={uploadedVideo?.duration}
						referenceVideoUrl={referenceVideo?.url}
						negativePromptOpen={negativePromptOpen}
						onSettingChange={gen.userUpdateSetting}
						onVideoDurationChange={setSelectedVideoDuration}
						onNegativePromptToggle={handleNegativePromptToggle}
						availableModels={availableModels}
						onSelectModel={gen.setSelectedModel}
						photoSlotsEngaged={gen.photoSlotsEngaged}
						onSeedanceModeChange={(mode) => switchSeedanceMode({ settings: gen.settings, updateSetting: gen.userUpdateSetting, setUploadedImage: gen.setUploadedImage, setEndImage: gen.setEndImage, setReferenceImages: gen.setReferenceImages, setReferenceVideos: gen.setReferenceVideos, setReferenceVideo: gen.setReferenceVideo }, mode)}
						referenceVideos={gen.referenceVideos}
						inputsAttached={inputsAttachedOf(gen)}
						draftAvailable={gen.draftAvailable}
						onVideoIntentChange={(intent) => switchVideoIntent({ model, settings: gen.settings, referenceVideos: gen.referenceVideos ?? [], updateSetting: gen.userUpdateSetting }, intent)}
						audioOperation={gen.audioOperation}
						sourceTrackModel={gen.sourceTrack?.model ?? null}
					/>
			</BarShell>

			{/* ── Пикер источника файла + галерея «Мои генерации» (общий хук) ── */}
			{pickerPortal}
			{galleryModal}
		</>
	)
}
